import { Close, KeyboardArrowUp } from "@mui/icons-material";
import { alpha, Badge, Box, ClickAwayListener, Collapse, IconButton, Tab, Tabs } from "@mui/material";
import { ReactNode } from "react";

import logo from "../../assets/images/flownectlogo.png";
import { useContactCenterTheme } from "./theming/ContactCenterThemeContext";
import { ContactCenterThemeScope } from "./theming/ContactCenterThemeScope";
import { TabConfigItem, VirtualRecepcionistTypes } from "./types";
import { useContactCenterDimensions } from "./useContactCenterDimensions";

const LOGO_FILTER = "brightness(0) saturate(100%) invert(55%) sepia(74%) saturate(927%) hue-rotate(345deg) brightness(95%) contrast(89%)";

const SHELL_SHADOW_COLLAPSED = "0 8px 24px rgba(0,0,0,0.14)";
const SHELL_SHADOW_EXPANDED = "0 24px 48px rgba(0,0,0,0.16), 0 8px 16px rgba(0,0,0,0.08)";

const COLLAPSE_TIMEOUT = { enter: 400, exit: 320 };

interface ContactCenterShellProps {
  expanded: boolean;
  totalUnreadMessages: number;
  hasUnreadMessages: boolean;
  zIndex: number;
  showTabs: boolean;
  type: VirtualRecepcionistTypes;
  filteredTabConfig: TabConfigItem[];
  onExpand: () => void;
  onCollapse: () => void;
  onRaise?: () => void;
  onTabChange: (value: VirtualRecepcionistTypes) => void;
  children: ReactNode;
}

export function ContactCenterShell({
  expanded,
  totalUnreadMessages,
  hasUnreadMessages,
  zIndex,
  showTabs,
  type,
  filteredTabConfig,
  onExpand,
  onCollapse,
  onRaise,
  onTabChange,
  children,
}: ContactCenterShellProps) {
  const { width, headerHeight, bodyHeight } = useContactCenterDimensions();
  const { theme } = useContactCenterTheme();

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (!expanded) return;

    const target = event.target as HTMLElement;

    if (
      target.closest(".MuiPopover-root, .MuiTooltip-popper, .MuiMenu-root, .MuiModal-root, .MuiDialog-root, [data-prevent-parent-trigger='true']")
    ) {
      return;
    }

    onCollapse();
  };

  // Keep the same element tree (always wrapped in ClickAwayListener) so React
  // never unmounts/remounts the Collapse subtree when toggling expanded state,
  // which would otherwise skip the transition entirely.
  return (
    <ContactCenterThemeScope>
      <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown" touchEvent="onTouchStart">
        <Box
          data-contact-center-shell
          onMouseDown={onRaise}
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            width,
            minWidth: width,
            maxWidth: width,
            zIndex,
            backgroundColor: theme.shell.bg,
            color: theme.shell.textPrimary,
            borderRadius: "16px",
            boxShadow: expanded ? SHELL_SHADOW_EXPANDED : SHELL_SHADOW_COLLAPSED,
            transition: `box-shadow ${COLLAPSE_TIMEOUT.enter}ms cubic-bezier(0, 0, 0.2, 1)`,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
              height: headerHeight,
              minHeight: headerHeight,
              px: 2.5,
              boxSizing: "border-box",
              backgroundColor: theme.shell.headerBg,
              animation: !expanded && hasUnreadMessages ? "ccBarPulse 2.8s ease-in-out infinite" : "none",

              "@keyframes ccBarPulse": {
                "0%": { boxShadow: `inset 0 0 0 0 ${alpha(theme.shell.accent, 0.2)}` },
                "70%": { boxShadow: `inset 0 0 0 4px ${alpha(theme.shell.accent, 0)}` },
                "100%": { boxShadow: `inset 0 0 0 0 ${alpha(theme.shell.accent, 0)}` },
              },
            }}
          >
            <Box sx={{ flexShrink: 0, display: "flex", position: "relative" }}>
              <Box component="img" src={logo} alt="logo-icon" sx={{ width: 25, height: 25, filter: LOGO_FILTER }} />

              {!expanded && hasUnreadMessages && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -4,
                    right: -6,
                    minWidth: 16,
                    height: 16,
                    px: totalUnreadMessages > 9 ? 0.35 : 0,
                    borderRadius: "999px",
                    bgcolor: theme.shell.accent,
                    color: "#fff",
                    border: `2px solid ${theme.shell.headerBg}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "9px",
                    fontWeight: 800,
                    lineHeight: 1,
                  }}
                >
                  {totalUnreadMessages > 99 ? "99+" : totalUnreadMessages}
                </Box>
              )}
            </Box>

            <Box
              component="span"
              onClick={() => {
                if (!expanded) onExpand();
              }}
              sx={{
                flex: 1,
                fontWeight: 700,
                fontSize: 15,
                color: theme.shell.accent,
                letterSpacing: "-0.01em",
                userSelect: "none",
                lineHeight: 1.2,
                cursor: expanded ? "default" : "pointer",
              }}
            >
              Contact Center
            </Box>

            <IconButton
              size="small"
              onClick={() => (expanded ? onCollapse() : onExpand())}
              aria-label={expanded ? "Collapse Contact Center" : "Expand Contact Center"}
              aria-expanded={expanded}
              sx={{
                width: 30,
                height: 30,
                flexShrink: 0,
                color: theme.shell.textSecondary,
                transition: "background-color 0.2s ease",
                "&:hover": { backgroundColor: alpha(theme.shell.accent, 0.1) },
              }}
            >
              {expanded ? (
                <Close sx={{ fontSize: 18, transition: "opacity 0.22s ease" }} />
              ) : (
                <KeyboardArrowUp sx={{ fontSize: 20, transition: "opacity 0.22s ease" }} />
              )}
            </IconButton>
          </Box>

          <Collapse
            in={expanded}
            timeout={COLLAPSE_TIMEOUT}
            collapsedSize={0}
            mountOnEnter
            unmountOnExit
            easing={{
              enter: "cubic-bezier(0, 0, 0.2, 1)",
              exit: "cubic-bezier(0.4, 0, 1, 1)",
            }}
          >
            <Box
              sx={{
                height: bodyHeight,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backgroundColor: theme.shell.bg,
              }}
            >
              {showTabs && (
                <Box sx={{ px: 2, mb: 0.5, flexShrink: 0 }}>
                  <Tabs
                    value={type}
                    onChange={(_, v: VirtualRecepcionistTypes) => onTabChange(v)}
                    aria-label="virtual receptionist tabs"
                    variant="fullWidth"
                    sx={{
                      minHeight: 0,
                      borderBottom: "1px solid",
                      borderColor: theme.shell.border,
                      "& .MuiTabs-indicator": {
                        backgroundColor: theme.shell.accent,
                        height: 2,
                        borderRadius: "2px 2px 0 0",
                      },
                      "& .MuiTabs-flexContainer": { gap: 0 },
                    }}
                  >
                    {filteredTabConfig.map(({ value, label, icon, badgeCount }) => (
                      <Tab
                        key={value}
                        disableRipple
                        icon={
                          badgeCount && badgeCount > 0 ? (
                            <Badge
                              badgeContent={badgeCount > 99 ? "99+" : badgeCount}
                              color="error"
                              sx={{
                                "& .MuiBadge-badge": {
                                  fontSize: "9px",
                                  height: "16px",
                                  minWidth: "16px",
                                  padding: "0 4px",
                                },
                              }}
                            >
                              {icon}
                            </Badge>
                          ) : (
                            <>{icon}</>
                          )
                        }
                        iconPosition="top"
                        label={label}
                        value={value}
                        sx={{
                          minWidth: 0,
                          minHeight: 0,
                          py: 1,
                          px: 0.25,
                          gap: "4px",
                          fontSize: 10,
                          fontWeight: 500,
                          textTransform: "none",
                          color: theme.shell.textSecondary,
                          transition: "color 0.2s",
                          "& .MuiTab-iconWrapper": { margin: 0 },
                          "&.Mui-selected": { color: theme.shell.accent },
                        }}
                      />
                    ))}
                  </Tabs>
                </Box>
              )}

              <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>{children}</Box>
            </Box>
          </Collapse>
        </Box>
      </ClickAwayListener>
    </ContactCenterThemeScope>
  );
}
